// ELVÁLASZTÓ VONAL — a rendszer EGYETLEN vízszintes/függőleges választó-vonala.
//
// MIÉRT EGY KOMPONENS (user-döntés): a vonalak addig ad-hoc `border-t`/`border-b`/`divide-y`
// osztályokként éltek szétszórva, ezért mindenhol MÁS volt a szín (`border-border`, `border-border/60`,
// `var(--table-hairline)`), a térköz és az, hogy a vonal beleér-e a konténer paddingjébe. Egy helyen definiálva
// egyszerre javítható mind — és a következő „ez a vonal nem tetszik" kérés EGY fájlt érint.
//
// ══ A KÉT SZABÁLY, AMI A DESIGNBÓL JÖN ═══════════════════════════════════════════════════════════════════
//   1. **BEHÚZOTT alapból** (`inset`): egy modálban/popoverben a vonal SOHA nem futhat ki a panel széléig —
//      a tartalom oldalt paddinget kap, a vonalnak vele egy vonalban kell kezdődnie/végződnie. Ezért a
//      komponens alapból `mx-3`-at ad, és a hívó `inset={false}`-szal kérhet teljes szélességet (pl. egy
//      táblázat-sor alatti hairline, ami szándékosan végig fut).
//   2. **Fejléc alatt NINCS vonal.** Modál, felugró panel és kártya fejléce alatt a hierarchiát a tipográfia
//      és a térköz adja (Linear/Stripe-minta) — a `DialogHeader` ezért eleve vonal nélküli. Ez a komponens
//      NEM arra van, hogy fejléc alá tegyük.
//
// A SZÍN token-alapú (`bg-border`), tehát világos és sötét módban is helyes — nincs hardcode-olt hex. A
// `hairline` tónus a táblázatok halványabb vonala (`--table-hairline`), hogy a sűrű listák ne „csíkosodjanak".
import { cn } from '@/lib/utils';

/** Térköz-lépcső a vonal körül (`my-*`). A `none` a listák közti vonalhoz (a sorok maguk adják a paddinget). */
export type DividerSpacing = 'none' | 'sm' | 'md' | 'lg';

const SPACING_Y: Record<DividerSpacing, string> = {
  none: '',
  sm: 'my-2',
  md: 'my-3',
  lg: 'my-4',
};

const SPACING_X: Record<DividerSpacing, string> = {
  none: '',
  sm: 'mx-2',
  md: 'mx-3',
  lg: 'mx-4',
};

export interface DividerProps {
  /** `horizontal` (alap) vagy `vertical` — a függőleges a szülő magasságát veszi fel. */
  orientation?: 'horizontal' | 'vertical';
  /** Térköz a vonal körül. Alap: `md`. */
  spacing?: DividerSpacing;
  /**
   * Behúzás a konténer széléről (alap: TRUE). Modálban/popoverben ez a helyes: a vonal ne érjen ki a panel
   * széléig. `false` → teljes szélesség (pl. tábla-sor hairline).
   */
  inset?: boolean;
  /** `default` = a felület vonala; `hairline` = a táblázatok halványabb vonala. */
  tone?: 'default' | 'hairline';
  className?: string | undefined;
}

export function Divider({
  orientation = 'horizontal',
  spacing = 'md',
  inset = true,
  tone = 'default',
  className,
}: DividerProps): JSX.Element {
  const horizontal = orientation === 'horizontal';
  return (
    <div
      // `role="separator"`: a képernyőolvasó tagolást mond (a `<hr>`-rel egyenértékű szemantika, de flexben
      // és gridben jól viselkedik). Dekoratív, tehát nincs neve.
      role="separator"
      aria-orientation={orientation}
      className={cn(
        'shrink-0',
        tone === 'hairline' ? 'bg-[color:var(--table-hairline)]' : 'bg-border',
        horizontal ? 'h-px' : 'w-px self-stretch',
        horizontal ? (inset ? 'mx-3' : 'w-full') : inset ? 'my-3' : '',
        horizontal ? SPACING_Y[spacing] : SPACING_X[spacing],
        className,
      )}
    />
  );
}
